import React, { useState, useContext } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { LoruleRequestConfig, RoleUtils } from '@galaxy/lorule-react-commons';
import { useMsal } from '@azure/msal-react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { ResponseError, SubmitButton, AsyncSelectField } from '@nintendo/forms';
import ConfigContext from '../../../contexts/ConfigContext';
import NinModal from '../../common/modal/NinModal';
import { sendToast, noOptionsHandler } from '../../../utils/displayUtils';
import TextCountedField from '../../common/TextCountedField';
import { COMMENT_MAX_LENGTH, INITIAL_SELECT_ID } from '../../../utils/Constants';
import { getAmUsers } from '../../../api/amUserApi';
import { replaceProjectManager } from '../../../api/actionMemoApi';

export interface ReplaceProjectManagerFormBaseProps {
    title: String;
    toggleButton: JSX.Element;
    onSubmit?: () => void;
}

export interface ReplaceProjectManagerModalProps extends ReplaceProjectManagerFormBaseProps {
    amId: number;
    amNumber: number;
    personId: number;
}

const submitReplaceProjectManager = async (
    config: LoruleRequestConfig,
    values: {
        amId: number;
        amNumber: number;
        personId: number;
        commentMessage: string | null;
    },
    roles: string[],
    setResponseError: (errorMessage: string) => void,
    resetForm: () => void,
    setSubmitting: (isSubmitting: boolean) => void,
    toggleModal: () => void,
) => {
    try {
        await replaceProjectManager(
            config,
            values.amId,
            values.personId,
            values.commentMessage ? values.commentMessage : null,
            !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
        );
        sendToast(
            <>
                Replaced Project Manager: <strong>{values.amNumber}</strong>
            </>,
        );

        resetForm();
        toggleModal();

        return true;
    } catch (error) {
        if (error instanceof Error) {
            setResponseError(`Replace user failed:  ${error.message}`);
        }
        setSubmitting(false);
        return false;
    }
};

const ReplaceProjectManagerModal = (props: ReplaceProjectManagerModalProps) => {
    const { instance } = useMsal();
    const config = useContext(ConfigContext);
    const roles = RoleUtils.getRoles(instance);

    const [responseError, setResponseError] = useState('');

    const addCommentValidationSchema = Yup.object().shape({
        personId: Yup.number()
            .required('Person is required')
            .test(
                'id validation',
                'Person is required',
                (personId) => personId !== INITIAL_SELECT_ID,
            ),
        commentMessage: Yup.string()
            // eslint-disable-next-line no-template-curly-in-string
            .max(COMMENT_MAX_LENGTH, 'Comment exceeds max length of ${max} characters'),
    });

    const initialValues = {
        amId: props.amId,
        amNumber: props.amNumber,
        personId: INITIAL_SELECT_ID,
        commentMessage: '',
    };

    return (
        <NinModal
            button={props.toggleButton}
            size="lg"
            render={(toggleModal: () => void) => (
                <Formik
                    initialValues={initialValues}
                    validationSchema={addCommentValidationSchema}
                    onSubmit={async (values, { resetForm, setSubmitting }) => {
                        const submitSuccessful = await submitReplaceProjectManager(
                            config,
                            values,
                            roles,
                            setResponseError,
                            resetForm,
                            setSubmitting,
                            toggleModal,
                        );
                        if (props.onSubmit && submitSuccessful) {
                            setResponseError('');
                            props.onSubmit();
                        }
                    }}
                    render={({ values, touched, errors, isSubmitting, dirty }) => (
                        <>
                            <Modal.Header closeButton>
                                <Modal.Title>Replace Project Manager</Modal.Title>
                            </Modal.Header>
                            <Form>
                                <Modal.Body>
                                    <ResponseError errorMessage={responseError} />

                                    <Alert variant="info">
                                        Please note that replacing the project manager will update
                                        approver lists with a new chain of command on any unapproved
                                        action memos with <b>AM# {props.amNumber}</b>. We recommend
                                        you review the approver lists after you replace the project
                                        manager.
                                    </Alert>

                                    <AsyncSelectField
                                        label="Person"
                                        name="personId"
                                        touched={touched.personId}
                                        errors={errors.personId}
                                        testId="personId"
                                        isRequired
                                        asyncSelectProps={{
                                            loadOptions: async (inputValue: string) => {
                                                if (inputValue.length > 2) {
                                                    const users = await getAmUsers(config, {
                                                        name: inputValue,
                                                    });
                                                    if (users) {
                                                        return users
                                                            .filter(
                                                                (user) =>
                                                                    user.personId !==
                                                                    props.personId,
                                                            )
                                                            .map((user) => ({
                                                                value: user.personId.toString(),
                                                                label: `${user.preferredName} (${user.title})`,
                                                            }));
                                                    }
                                                }
                                                return [];
                                            },
                                            noOptionsMessage: noOptionsHandler,
                                        }}
                                    />
                                    <TextCountedField
                                        label="Comment (Optional)"
                                        name="commentMessage"
                                        touched={touched.commentMessage}
                                        errors={errors.commentMessage}
                                        testId="commentMessage"
                                        length={
                                            values.commentMessage ? values.commentMessage.length : 0
                                        }
                                        maxLength={COMMENT_MAX_LENGTH}
                                    />
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={toggleModal}
                                        disabled={isSubmitting}
                                    >
                                        Close
                                    </Button>
                                    <SubmitButton
                                        className="btn-primary ml-auto"
                                        disabled={!dirty}
                                        isSubmitting={isSubmitting}
                                        buttonText="Replace User"
                                    />
                                </Modal.Footer>
                            </Form>
                        </>
                    )}
                />
            )}
        />
    );
};

export default ReplaceProjectManagerModal;
